import { motion } from "framer-motion";
import { PhoneCall, Sparkles, Smile } from "lucide-react";
import { Reveal } from "./shared";

const steps = [
  {
    icon: PhoneCall,
    num: "01",
    title: "Call for a Quote",
    text: "Tell us about your space and needs. We'll give you a clear, flat-rate price — no surprises.",
    img: "https://images.unsplash.com/photo-1588346985272-e31baa849baa?crop=entropy&cs=srgb&fm=jpg&w=700&q=85",
    blob: "62% 38% 34% 66% / 58% 34% 66% 42%",
    tint: "from-pink/50 to-blush",
  },
  {
    icon: Sparkles,
    num: "02",
    title: "We Clean with Care",
    text: "Our trained team arrives fully equipped and treats your home as gently as if it were our own.",
    img: "https://images.unsplash.com/photo-1758273238564-806f750a2cce?crop=entropy&cs=srgb&fm=jpg&w=700&q=85",
    blob: "38% 62% 63% 37% / 41% 44% 56% 59%",
    tint: "from-teal/40 to-blush",
  },
  {
    icon: Smile,
    num: "03",
    title: "Enjoy Your Space",
    text: "Walk into a fresh, spotless home and simply relax. Satisfaction guaranteed, every time.",
    img: "https://images.unsplash.com/photo-1758687126877-b37052a20a4d?crop=entropy&cs=srgb&fm=jpg&w=700&q=85",
    blob: "52% 48% 43% 57% / 60% 42% 58% 40%",
    tint: "from-pink/50 to-blush",
  },
];

export default function HowItWorks() {
  return (
    <section id="how-it-works" data-testid="how-it-works" className="relative bg-white py-24 md:py-32 overflow-hidden">
      {/* soft decorative blobs */}
      <div className="pointer-events-none absolute -top-16 -left-16 w-72 h-72 rounded-full bg-blush/40 blur-3xl animate-floaty" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-80 h-80 rounded-full bg-pink/20 blur-3xl animate-floaty-slow" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <Reveal className="text-center max-w-2xl mx-auto mb-20">
          <p className="text-sm uppercase tracking-[0.25em] text-pink-dark font-semibold mb-4">Simple &amp; Stress-Free</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-teal font-medium">Easy steps to a clean home</h2>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-16 md:gap-8 relative">
          {/* connecting dotted line (desktop) */}
          <div className="hidden md:block absolute top-32 left-[16%] right-[16%] h-px border-t-2 border-dashed border-blush -z-0" />

          {steps.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.15}>
              <div className="relative flex flex-col items-center text-center px-2" data-testid={`step-${i}`}>
                {/* blob image */}
                <motion.div
                  whileHover={{ scale: 1.03, rotate: i % 2 === 0 ? 1.5 : -1.5 }}
                  transition={{ type: "spring", stiffness: 200, damping: 18 }}
                  className="relative w-60 h-60 sm:w-64 sm:h-64"
                >
                  {/* colored outline blob behind */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${s.tint} scale-[1.08] rotate-6`}
                    style={{ borderRadius: s.blob }}
                  />
                  <div
                    className="absolute inset-0 overflow-hidden shadow-[0_18px_45px_rgba(43,81,86,0.14)]"
                    style={{ borderRadius: s.blob }}
                  >
                    <img
                      src={s.img}
                      alt={s.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>

                  {/* step number badge */}
                  <span className="absolute -top-3 -left-1 z-10 font-serif-display text-2xl text-teal bg-white rounded-full w-14 h-14 flex items-center justify-center shadow-md border border-blush">
                    {s.num}
                  </span>
                  {/* icon chip */}
                  <span className="absolute -bottom-3 right-4 z-10 w-12 h-12 rounded-full bg-teal text-white flex items-center justify-center shadow-lg">
                    <s.icon size={20} strokeWidth={1.5} />
                  </span>
                </motion.div>

                <h3 className="font-serif-display text-2xl text-teal mt-10 mb-3">{s.title}</h3>
                <p className="text-teal-soft leading-relaxed max-w-xs">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
